import React, { useState, useEffect } from "react";
import {
  Card,
  InputGroup,
  Col,
  Row,
  Spinner,
  Modal,
  Button,
  Form,
  Accordion,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FalconComponentCard from "components/common/falconComponentCard";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Divider from "components/common/divider";
import { toast } from "react-toastify";
import { fetchRoleMappings, useLocationRedirect } from "../../assets/js/common";
import EmailConfigurationDetailsModal from "./emailConfigurationDetailsModal"
import SignatureModal from "./signatureModal"
import FinancialInformationConfigurationModal from "./financialInformationConfigurationModal"
import { useAppContext } from '../../providers/appProvider';
import Select from "react-select";
import { getDomainConfig } from "../../config/domainConfig";
import { requestNotificationPermission } from "utils/pushNotification";

const Profile = () => {
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(true);
  const [isUpdatingPushNotification, setIsUpdatingPushNotification] = useState(false);
  const [isInAppChannelEnabled, setIsInAppChannelEnabled] = useState(true);

  useEffect(() => {
    const handleInAppToggled = (event) => {
      if (typeof event?.detail?.enabled === "boolean") {
        setIsInAppChannelEnabled(event.detail.enabled);
      }
    };
    window.addEventListener("inAppChannelToggled", handleInAppToggled);
    return () => {
      window.removeEventListener("inAppChannelToggled", handleInAppToggled);
    };
  }, []);

  const handleMainPushToggle = async (enabled) => {
    if (!userId || !isInAppChannelEnabled) return;

    const previousState = pushNotificationsEnabled;
    setPushNotificationsEnabled(enabled);
    setIsUpdatingPushNotification(true);

    const browserPermission = enabled ? await requestNotificationPermission() : null;

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('Token');

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateNotificationPreferences/${userId}`,
        {
          pushNotificationEnabled: enabled,
          ...(token ? { token, userToken: token } : {}),
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
            "Content-Type": "application/json",
            ...(token
              ? {
                  "x-user-token": token,
                  "x-access-token": token,
                  "x-auth-token": token,
                  "user-token": token,
                  "userToken": token,
                  token: token,
                }
              : {}),
          },
        }
      );

      setData((prev) => ({
        ...prev,
        PushNotificationEnabled: enabled,
      }));

      toast.success(t("pushNotificationUpdateSuccess"), {
        position: "top-right",
        autoClose: 3000,
      });
      if (enabled && browserPermission !== "granted") {
        toast.warning(t("pushNotificationBrowserBlocked"), {
          position: "top-right",
          autoClose: 8000,
        });
      }
    } catch (error) {
      console.error("Failed to update notification preferences:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.response?.data?.message,
      });
      setPushNotificationsEnabled(previousState);
      const errorMessage =
        error?.response?.data?.message || t("pushNotificationUpdateFailed");
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUpdatingPushNotification(false);
    }
  };
  const [roleId, setRoleId] = useState("");
  const [roleText, setRoleText] = useState("");
  const { t, i18n } = useTranslation();
  const { setConfig } = useAppContext();

  const [smtpServerDetails, setSmtpServerDetails] = useState(data ? data.SmtpServer : null);
  const [portDetails, setPortDetails] = useState(data ? data.PortNumber : null);
  const [senderEmailPassword, setSenderEmailPassword] = useState(
    data.SenderEmailPassword ? atob(data.SenderEmailPassword) : null
  );
  const [senderEmail, setSenderEmail] = useState(data ? data.SenderEmail : null);
  const [isEmailConfigEnabled, setIsEmailConfigEnabled] = useState(false);
  const [isEmailConfigSaved, setIsEmailConfigSaved] = useState(false);
  const [showRemoveEmailConfirm, setShowRemoveEmailConfirm] = useState(false);
  const [isEditingEmailConfig, setIsEditingEmailConfig] = useState(false);
  const isCustomer = data?.IsCustomer === true;
  const isCustomEmailEnabled = data?.CustomEmailConfigEnabled === true;
  const [isSavingEmailConfig, setIsSavingEmailConfig] = useState(false);

  const hasUserEmailConfig =
    !!data?.SenderEmail &&
    !!data?.SmtpServer &&
    !!data?.PortNumber;
  const emailSource =
    isCustomer || (isCustomEmailEnabled && hasUserEmailConfig)
      ? "USER"
      : "APP_OWNER";

  const LANGUAGE_MAP = {
    EN: t("languageEnglish"),
    NL: t("languageDutch"),
    HI: t("languageHindi")
  };

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [companySignature, setCompanySignature] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("EN");
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [tempLanguage, setTempLanguage] = useState("EN");

  // finance info
  const [ibanDetails, setIbanDetails] = useState(data ? data.IbanDetails : null);
  const [gaccountDetails, setGaccountDetails] = useState(data ? data.GaccountDetails : null);
  const [kvkDetails, setKvkDetails] = useState(data ? data.KvkDetails : null);
  const [btwDetails, setBtwDetails] = useState(data ? data.BtwDetails : null);
  const [accountDetails, setAccountDetails] = useState(data?.AccountDetails || null); 
  const [ifscDetails, setIfscDetails] = useState(data?.IfscDetails || null);
  const [gstDetails, setGstDetails] = useState(data?.GstDetails || null);
  const userRole = localStorage.getItem("UserRole");
  const isEmployeeView = sessionStorage.getItem("isEmployeeView") === "true";
  const domainConfig = getDomainConfig();
  const finLabels = domainConfig.financialLabels;

  // smtp field-level errors
  const [smtpFieldErrors, setSmtpFieldErrors] = useState({});

  const SMTP_FIELD_MAP = {
    smtpServer: { key: 'tempSmtpServerDetails', message: t("smtpServerInvalidText") },
    portNumber: { key: 'tempPortDetails', message: t("portNumberInvalidText") },
    senderEmailPassword: { key: 'tempSenderEmailPassword', message: t("senderEmailPasswordInvalidText") },
  };

  useLocationRedirect("dashboard");

  useEffect(() => {
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId) {
      setUserId(storedUserId);
    }
  }, []);

  const resetPasswordVisibility = () => {
    setPasswordVisibility({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
  };

  const fetchUserData = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setRoleId(response.data.Role);
      setData(response.data);
      setPushNotificationsEnabled(response.data.PushNotificationEnabled ?? true);
      setCompanySignature(response.data.CompanySignature)
      setSelectedLanguage(response.data.DefaultLanguage);

      const targetCustomerId =
        localStorage.getItem("customerId") ||
        sessionStorage.getItem("customerId") ||
        response.data?.CustomerId ||
        response.data?.customerId;

      if (targetCustomerId) {
        try {
          const settingsRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/notificationSettingsRoute/getSettings/${targetCustomerId}`,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );
          const inAppChannel = settingsRes.data?.channels?.find((c) => c.Channel === "InApp");
          setIsInAppChannelEnabled(inAppChannel !== undefined ? !!inAppChannel.Enabled : true);
        } catch (settingsError) {
          console.error("Error fetching notification settings for profile:", settingsError);
          setIsInAppChannelEnabled(true);
        }
      }

      setLoading(false);
      const roleName = await getRoleText(response.data.Role);
      setRoleText(roleName);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserData(userId);
    }
  }, [userId, i18n.language]);

  useEffect(() => {
    setIsEmailConfigEnabled(isCustomEmailEnabled);

    if (hasUserEmailConfig) {
      setSenderEmail(data.SenderEmail);
      setSmtpServerDetails(data.SmtpServer);
      setPortDetails(data.PortNumber);
      setSenderEmailPassword(
        data.SenderEmailPassword ? atob(data.SenderEmailPassword) : null
      );
    }
  }, [isCustomEmailEnabled, data]);

  const regex =
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleInputChange = (field, value) => {
    setPasswords((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const validateField = (field, value) => {
    let error = "";

    if (!value) {
      setErrors((prev) => ({ ...prev, [field]: t("fieldRequiredText") }));
      return;
    }

    if (field === "currentPassword" && !regex.test(value)) {
      setErrors((prev) => ({ ...prev, [field]: t("passwordCriteriaText") }));
      return;
    }

    if (field === "newPassword" && !regex.test(value)) {
      setErrors((prev) => ({ ...prev, [field]: t("passwordCriteriaText") }));
      return;
    }

    if (field === "confirmPassword" && value !== passwords.newPassword) {
      setErrors((prev) => ({ ...prev, [field]: t("passwordMismatchText") }));
      return;
    }

    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const fields = Object.keys(passwords);
    let isValid = true;

    fields.forEach((field) => {
      validateField(field, passwords[field]);
      if (errors[field]) isValid = false;
    });

    return isValid;
  };

  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const handleSaveLanguage = async () => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateDefaultLanguage`,
        { userId, language: tempLanguage, performedBy: localStorage.getItem("UserId") },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      setSelectedLanguage(tempLanguage);

      const culture = tempLanguage.toLowerCase();
      setConfig("language", culture);

      toast.success(t("languageUpdateSuccess"), {
        position: "top-right",
        autoClose: 3000,
      });
      setShowLanguageModal(false);
    } catch {
      toast.error(t("languageUpdateFailed"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const hashedCurrentPassword = await hashPassword(passwords.currentPassword);
      const hashedNewPassword = await hashPassword(passwords.newPassword);

      if (hashedCurrentPassword !== data.Password) {
        setErrors((prev) => ({
          ...prev,
          currentPassword: t("incorrectCurrentPasswordText"),
        }));
        return;
      }

      if (!validateForm()) return;

      const requestData = { userId, newPassword: hashedNewPassword, performedBy: localStorage.getItem("UserId") };

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/resetPassword`,
        requestData, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      if (response.status === 200) {
        toast.success(t("passwordResetSuccessMessage"), {
          position: "top-right",
          autoClose: 5000,
        });
        clearResetPasswordForm();
        setShowPasswordModal(false);
      } else {
        console.error("Error resetting password:", response.data);
      }
    } catch (error) {
      console.error("Error processing password reset request:", error);
    }
  };

  const clearResetPasswordForm = () => {
    setPasswords({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({});
    resetPasswordVisibility();
  };

  const handleDiscard = () => {
    clearResetPasswordForm();
    setShowPasswordModal(false);
  };

  useEffect(() => {
    if (userId) {
      fetchUserData(userId);
    }
  }, [userId]);

  const getRoleText = async (role) => {
    try {
      const roleName = await fetchRoleMappings(role, t); // Fetch role name from common.js
      // return roleTranslations[roleName] || roleName;  // Return translated or original role name
      return roleName;
    } catch (error) {
      console.error("Error fetching roles:", error);
      return t("unknownText") || "Onbekend";
    }
  };

  const [showEmailConfigurationDetailsModal, setShowEmailConfigurationDetailsModal] =
    useState(false);

  // Open Modal Handler
  const handleEmailConfigurationDetailsOpenModal = () => setShowEmailConfigurationDetailsModal(true);

  const handleEmailConfigurationDetailsCloseModal = () => {
    setShowEmailConfigurationDetailsModal(false);
    setSmtpFieldErrors({});
    if (!hasUserEmailConfig) {
      setIsEmailConfigEnabled(false);
    }
  };

  const handleClearSmtpError = (field) => {
    setSmtpFieldErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleEmailConfigurationDetailsSaveModal = async (updatedDetails) => {
    setIsSavingEmailConfig(true);

    try {
      const updateUserData = {
        smtpServer: updatedDetails.tempSmtpServerDetails,
        portNumber: updatedDetails.tempPortDetails,
        senderEmail: updatedDetails.tempSenderEmail,
        senderEmailPassword: btoa(updatedDetails.tempSenderEmailPassword),
        performedBy: localStorage.getItem("UserId"),
      };

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateEmailConfigurationByUserId/${userId}`,
        updateUserData,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      if (response.status === 200) {
        const updatedUserEmailData = {
          SenderEmail: updatedDetails.tempSenderEmail,
          SmtpServer: updatedDetails.tempSmtpServerDetails,
          PortNumber: updatedDetails.tempPortDetails,
          SenderEmailPassword: btoa(updatedDetails.tempSenderEmailPassword),
        };

        setSenderEmail(updatedUserEmailData.SenderEmail);
        setSmtpServerDetails(updatedUserEmailData.SmtpServer);
        setPortDetails(updatedUserEmailData.PortNumber);
        setSenderEmailPassword(updatedDetails.tempSenderEmailPassword);

        await enableCustomEmail(true);

        setData(prev => ({
          ...prev,
          ...updatedUserEmailData,
          CustomEmailConfigEnabled: true,
        }));

        setIsEmailConfigSaved(true);
        setIsEmailConfigEnabled(true);
        setIsEditingEmailConfig(false);
        setShowEmailConfigurationDetailsModal(false);
        setSmtpFieldErrors({});

        toast.success(t("emailConfigurationSuccessMessage"), {
          position: "top-right",
          autoClose: 3000,
        });
      }

    } catch (error) {
      if (
        error.response?.status === 404 &&
        error.response?.data?.message === "invalidEmailDetails"
      ) {
        const fieldEntry = SMTP_FIELD_MAP[error.response.data.field];
        if (fieldEntry) {
          setSmtpFieldErrors({ [fieldEntry.key]: fieldEntry.message });
        }
      } else {
        toast.error(t("invalidEmailConfigurationMessage"), {
          position: "top-right",
          autoClose: 5000,
        });
      }
      console.error("Error updating email configuration:", error);
    } finally {
      setIsSavingEmailConfig(false);
    }
  };

  // Financial Info Modal
  const [showFinancialInformationConfigurationModal, setShowFinancialInformationConfigurationModal] =
    useState(false);

  const handleFinancialInformationConfigurationOpenModal = () =>
    setShowFinancialInformationConfigurationModal(true);

  const handleFinancialInformationConfigurationCloseModal = () =>
    setShowFinancialInformationConfigurationModal(false);

  const handleFinancialInformationConfigurationSaveModal = async (updatedDetails) => {
    const isIndianDomain = finLabels?.showIfsc === true;

    try {
      const performedBy = localStorage.getItem("UserId");

      const updateUserData = isIndianDomain
        ? {
            accountDetails: updatedDetails.tempAccountDetails,
            ifscDetails: updatedDetails.tempIfscDetails,
            gstDetails: updatedDetails.tempGstDetails,
            performedBy,
          }
        : {
            ibanDetails: updatedDetails.tempIbanDetails,
            gaccountDetails: updatedDetails.tempGaccountDetails,
            kvkDetails: updatedDetails.tempKvkDetails,
            btwDetails: updatedDetails.tempBtwDetails,
            performedBy,
          };

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateFinancialInformationConfigurationByUserId/${userId}`,
        updateUserData,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      if (response.status === 200) {
        const {
          IbanDetails, GaccountDetails, KvkDetails, BtwDetails,
          AccountDetails, IfscDetails, GstDetails,
        } = response.data;

        setIbanDetails(IbanDetails);
        setGaccountDetails(GaccountDetails);
        setKvkDetails(KvkDetails);
        setBtwDetails(BtwDetails);
        setAccountDetails(AccountDetails);
        setIfscDetails(IfscDetails);
        setGstDetails(GstDetails);
        setData((prev) => ({
          ...prev,
          IbanDetails, GaccountDetails, KvkDetails, BtwDetails,
          AccountDetails, IfscDetails, GstDetails,
        }));

        toast.success(t("financialInformationConfigurationSuccessMessage"), {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      toast.error(t("invalidFinancialInformationConfigurationMessage"), {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Error updating FinancialInformationConfiguration details:", error);
    }
  };

  const handleEmailSwitchChange = async (checked) => {
    if (checked) {
      if (hasUserEmailConfig) {
        await enableCustomEmail(true);
      } else {
        setIsEditingEmailConfig(false);
        handleEmailConfigurationDetailsOpenModal();
      }
    } else {
      await enableCustomEmail(false);
    }
  };

  const enableCustomEmail = async (enabled) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateCustomEmailConfigFlag/${userId}`,
        { customEmailConfigEnabled: enabled },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      setData(prev => ({
        ...prev,
        CustomEmailConfigEnabled: enabled
      }));

      setIsEmailConfigEnabled(enabled);
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    }
  };

  const openLanguageModal = () => {
    setTempLanguage(selectedLanguage);
    setShowLanguageModal(true);
  };

  const LANGUAGE_LABEL_MAP = {
    EN: t("languageEnglish"),
    NL: t("languageDutch"),
    HI: t("languageHindi"),
  };

  const languageOptions = domainConfig.supportedLanguages.map((lang) => ({
    value: lang,
    label: LANGUAGE_LABEL_MAP[lang] || lang,
  }));

  const isEmailConfigurationMissing =
    (!senderEmail && !data?.SenderEmail) ||
    (!smtpServerDetails && !data?.SmtpServer) ||
    (!portDetails && !data?.PortNumber);

  const isSignatureMissing = !companySignature;
  const shouldShowEmailCard = isCustomEmailEnabled;

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background
              image={corner4}
              className="bg-card"
              style={{
                borderTopRightRadius: "0.375rem",
                borderBottomRightRadius: "0.375rem",
              }}
            />
            <Card.Body className="position-relative pe-4 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("profileText")}</h5>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <FalconComponentCard>
            <Card.Header>
              <h5>{t("userInformationText")}</h5>
            </Card.Header>

            {!loading && userRole ? (
              userRole === "Employee" || isEmployeeView ? (
                <FalconComponentCard.Body>
                  <Row className="g-4">
                    <Col lg={8}>
                      <Card className="w-100 h-100 card-hover">
                        <Card.Body>
                          <Row className="mb-3">
                            <Col md={3}>
                              <h6 className="mb-1 data-font-size text-muted">{t("userNameText")}</h6>
                              <span className="data-font-size data-font-colour">{data.Name}</span>
                            </Col>
                            <Col md={3}>
                              <h6 className="mb-1 data-font-size text-muted">{t("roleText")}</h6>
                              <span className="data-font-size data-font-colour">{roleText}</span>
                            </Col>
                            <Col md={6}>
                              <h6 className="mb-1 data-font-size text-muted">{t("email")}</h6>
                              <span className="data-font-size data-font-colour">{data.Emailaddress}</span>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col lg={2}>
                      <Card className="w-100 h-100 card-hover">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-1 data-font-size text-muted">{t("password")}</h6>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-600"
                              onClick={() => setShowPasswordModal(true)}
                            >
                              <FontAwesomeIcon icon="pencil-alt" />
                            </Button>
                          </div>
                          <span className="d-block mb-0 pt-2 data-font-colour">********</span>
                        </Card.Body>
                      </Card>
                    </Col>

                    {domainConfig.showLanguageSwitcher && (
                    <Col lg={2}>
                      <Card className="w-100 h-100 card-hover">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-1 data-font-size text-muted">{t("Standaardtaal")}</h6>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-600"
                              onClick={openLanguageModal}
                            >
                              <FontAwesomeIcon icon="pencil-alt" />
                            </Button>
                          </div>
                          <span className="data-font-size pt-2 data-font-colour">
                            {LANGUAGE_MAP[selectedLanguage] || selectedLanguage}
                          </span>
                        </Card.Body>
                      </Card>
                    </Col>
                    )}
                  </Row>
                </FalconComponentCard.Body>
              ) : (
                <FalconComponentCard.Body>
                  <Row className="g-4">
                    <Col lg={12}>
                      <Card className="w-100 h-100 card-hover">
                        <Card.Body>
                          <Row>
                            <Col>
                              <h6 className="mb-1 data-font-size text-muted">{t("userNameText")}</h6>
                              <span className="data-font-size data-font-colour">{data.Name}</span>
                            </Col>
                            <Col>
                              <h6 className="mb-1 data-font-size text-muted">{t("roleText")}</h6>
                              <span className="data-font-size data-font-colour">{roleText}</span>
                            </Col>
                            <Col>
                              <h6 className="mb-1 data-font-size text-muted">{t("email")}</h6>
                              <span className="data-font-size data-font-colour">{data.Emailaddress}</span>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col lg={4}>
                      <Card className="w-100 h-100 card-hover">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-1 data-font-size text-muted">{t("password")}</h6>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-600"
                              onClick={() => setShowPasswordModal(true)}
                            >
                              <FontAwesomeIcon icon="pencil-alt" />
                            </Button>
                          </div>
                          <span className="d-block mb-0 pt-2 data-font-colour">********</span>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col lg={4}>
                      <Card
                        data-tour="tour-signature-card"
                        className={`w-100 h-100 ${isSignatureMissing ? "card-missing-data" : "card-hover"}`}
                      >
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1 data-font-size text-muted">{t("userSignature")}</h6>
                              <small className="text-muted d-block">
                                {t("signatureHelperText")}
                              </small>
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-600"
                              onClick={() => setShowSignatureModal(true)}
                            >
                              <FontAwesomeIcon icon="pencil-alt" />
                            </Button>
                          </div>
                          <span className="d-block mb-0 pt-2 data-font-size data-font-colour">
                            {companySignature ? (
                              <img
                                src={
                                  companySignature.startsWith("data:")
                                    ? companySignature         
                                    : `data:image/png;base64,${companySignature}` 
                                }
                                alt="Handtekening van klant"
                                className="p-2 mt-2 signature"
                              />
                            ) : (
                              <div style={{ paddingTop: "20px", color: "#666", fontStyle: "italic" }}>
                                <p className="mb-2">_________________________________</p>
                              </div>
                            )}
                          </span>
                        </Card.Body>
                      </Card>
                    </Col>

                    {domainConfig.showLanguageSwitcher && (
                    <Col lg={4}>
                      <Card className="w-100 h-100 card-hover">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-1 data-font-size text-muted">{t("Standaardtaal")}</h6>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-600"
                              onClick={openLanguageModal}
                            >
                              <FontAwesomeIcon icon="pencil-alt" />
                            </Button>
                          </div>
                          <span className="data-font-size pt-2 data-font-colour">
                            {LANGUAGE_MAP[selectedLanguage] || selectedLanguage}
                          </span>
                        </Card.Body>
                      </Card>
                    </Col>
                    )}
                  </Row>
                </FalconComponentCard.Body>
              )
            ) : null}
          </FalconComponentCard>

          <FalconComponentCard>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">{t("pushNotificationInformation")}</h5>
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip>{t("pushNotificationInfoNote")}</Tooltip>}
                  >
                    <span className="cursor-pointer text-primary">
                      <FontAwesomeIcon icon="info-circle" />
                    </span>
                  </OverlayTrigger>
                </div>
                <small className="text-muted">{t("pushNotificationHelperText")}</small>
              </div>
              {!isInAppChannelEnabled ? (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="profile-push-notification-disabled-tooltip">
                      {t("pushNotificationRequiresInApp")}
                    </Tooltip>
                  }
                >
                  <span className="d-inline-block">
                    <Form.Check
                      type="switch"
                      id="profile-push-notification-switch"
                      className="mb-0"
                      checked={false}
                      disabled
                      style={{ pointerEvents: "none" }}
                    />
                  </span>
                </OverlayTrigger>
              ) : (
                <Form.Check
                  type="switch"
                  id="profile-push-notification-switch"
                  className="mb-0"
                  checked={pushNotificationsEnabled}
                  disabled={isUpdatingPushNotification}
                  onChange={(e) => handleMainPushToggle(e.target.checked)}
                />
              )}
            </Card.Header>
          </FalconComponentCard>

          {!loading && userRole && (
            <>
              <FalconComponentCard>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="mb-0">{t("emailInformationText")}</h5>
                      <OverlayTrigger
                        placement="right"
                        overlay={<Tooltip>{t("emailConfigInfoNote")}</Tooltip>}
                      >
                        <span className="cursor-pointer text-primary">
                          <FontAwesomeIcon icon="info-circle" />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <small className="text-muted">{t("customEmailHelperText")}</small>
                  </div>
                  <Form.Check
                    type="switch"
                    id="profile-custom-email-switch"
                    className="mb-0"
                    checked={isCustomEmailEnabled}
                    onChange={(e) => handleEmailSwitchChange(e.target.checked)}
                  />
                </Card.Header>

                {shouldShowEmailCard && (
                  <FalconComponentCard.Body>
                    <Card
                      className={`w-100 h-100 ${isEmailConfigurationMissing ? "card-missing-data" : "card-hover"}`}
                    >
                      <Card.Body className="flex-fill">
                        <div className="d-flex justify-content-end align-items-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-600"
                            onClick={() => {
                              setIsEditingEmailConfig(hasUserEmailConfig);
                              handleEmailConfigurationDetailsOpenModal();
                            }}
                          >
                            <FontAwesomeIcon icon="pencil-alt" />
                          </Button>
                        </div>

                        <Row>
                          <Col lg xxl={6}>
                            <Row>
                              <Col xs={6} sm={5}>
                                <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                  {t("senderEmailText")}
                                </h6>
                              </Col>
                              <Col xs={6} sm={5}>
                                <p className="d-block mb-1 data-font-colour data-font-size">
                                  {senderEmail || data?.SenderEmail || "--"}
                                </p>
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={6} sm={5}>
                                <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                  {t("password")}
                                </h6>
                              </Col>
                              <Col xs={6} sm={5}>
                                <p className="d-block mb-1 data-font-colour data-font-size">
                                  ********
                                </p>
                              </Col>
                            </Row>
                          </Col>

                          <Col lg xxl={6}>
                            <Row>
                              <Col xs={6} sm={5}>
                                <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                  {t("smtpServerText")}
                                </h6>
                              </Col>
                              <Col xs={6} sm={5}>
                                <p className="d-block mb-1 data-font-colour data-font-size">
                                  {smtpServerDetails || data?.SmtpServer || "--"}
                                </p>
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={6} sm={5}>
                                <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                  {t("portNumberText")}
                                </h6>
                              </Col>
                              <Col xs={6} sm={5}>
                                <p className="d-block mb-1 data-font-colour data-font-size">
                                  {portDetails || data?.PortNumber || "--"}
                                </p>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </FalconComponentCard.Body>
                )}
              </FalconComponentCard>

              {!loading && userRole !== "Employee" && !isEmployeeView && (
                <FalconComponentCard>
                  <Card.Header className="d-flex align-items-center gap-2">
                    <h5 className="mb-0">{t("financialInformation")}</h5>
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip>
                          {t("financialInfoNote") ||
                            "The information provided above will be included in all proposals shared with your clients."}
                        </Tooltip>
                      }
                    >
                      <span className="cursor-pointer text-primary">
                        <FontAwesomeIcon icon="info-circle" />
                      </span>
                    </OverlayTrigger>
                  </Card.Header>
                  <FalconComponentCard.Body>
                    <Card>
                      <Card.Body className="flex-fill card-hover">
                        <div className="d-flex justify-content-end align-items-center">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-600"
                            onClick={() => handleFinancialInformationConfigurationOpenModal()}
                          >
                            <FontAwesomeIcon icon="pencil-alt" />
                          </Button>
                        </div>
                        <Row>
                          <Col lg xxl={6}>

                            {/* NL — IBAN */}
                            {!finLabels.showIfsc && (
                              <Row>
                                <Col xs={6} sm={5}>
                                  <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">{t(finLabels.iban)}</h6>
                                </Col>
                                <Col xs={6} sm={5}>
                                  <p className="d-block mb-1 data-font-colour data-font-size">
                                    {ibanDetails || data?.IbanDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* IN — Account Number */}
                            {finLabels.showIfsc && (
                              <Row>
                                <Col xs={6} sm={5}>
                                  <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">{t(finLabels.account)}</h6>
                                </Col>
                                <Col xs={6} sm={5}>
                                  <p className="d-block mb-1 data-font-colour data-font-size">
                                    {accountDetails || data?.AccountDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* IN — IFSC */}
                            {finLabels.showIfsc && (
                              <Row>
                                <Col xs={6} sm={5}>
                                  <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">{t(finLabels.ifsc)}</h6>
                                </Col>
                                <Col xs={6} sm={5}>
                                  <p className="d-block mb-1 data-font-colour data-font-size">
                                    {ifscDetails || data?.IfscDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* NL — G-Account */}
                            {finLabels.showGAccount && (
                              <Row>
                                <Col xs={6} sm={5}>
                                  <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">{t(finLabels.gAccount)}</h6>
                                </Col>
                                <Col xs={6} sm={5}>
                                  <p className="d-block mb-1 data-font-colour data-font-size">
                                    {gaccountDetails || data?.GaccountDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}
                          </Col>
                          <Col lg xxl={6}>

                            {/* NL — KVK/CoC */}
                            {finLabels.showKvk && (
                              <Row>
                                <Col xs={6} sm={5}>
                                  <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">{t("chamberOfCommerce")}</h6>
                                </Col>
                                <Col xs={6} sm={5}>
                                  <p className="d-block mb-1 data-font-colour data-font-size">
                                    {kvkDetails || data?.KvkDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* NL — VAT/BTW */}
                            {!finLabels.showIfsc && (
                              <Row>
                                <Col xs={6} sm={5}>
                                  <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">{t(finLabels.vat)}</h6>
                                </Col>
                                <Col xs={6} sm={5}>
                                  <p className="d-block mb-1 data-font-colour data-font-size">
                                    {btwDetails || data?.BtwDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* IN — GST */}
                            {finLabels.showIfsc && (
                              <Row>
                                <Col xs={6} sm={5}>
                                  <h6 className="fw-semi-bold mb-1 data-font-colour data-font-size">{t(finLabels.gst)}</h6>
                                </Col>
                                <Col xs={6} sm={5}>
                                  <p className="d-block mb-1 data-font-colour data-font-size">
                                    {gstDetails || data?.GstDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </FalconComponentCard.Body>
                </FalconComponentCard>
              )}
            </>
          )}

          {/* Reset Password Modal */}
          <Modal show={showPasswordModal} onHide={handleDiscard} backdrop="static">
            <Modal.Header closeButton>
              <Modal.Title>{t("updatePasswordText")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md="12" className="mb-3">
                    <Form.Label className="data-font-size data-font-colour">
                      {t("currentPasswordText")} <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={passwordVisibility.currentPassword ? "text" : "password"}
                        value={passwords.currentPassword}
                        onChange={(e) => handleInputChange("currentPassword", e.target.value)}
                        isInvalid={!!errors.currentPassword}
                      />
                      <InputGroup.Text onClick={() => togglePasswordVisibility("currentPassword")}>
                        {passwordVisibility.currentPassword ? <FaEyeSlash /> : <FaEye />}
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.currentPassword}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Col>

                  <Col md="12" className="mb-3">
                    <Form.Label className="data-font-size data-font-colour">
                      {t("newPasswordText")} <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={passwordVisibility.newPassword ? "text" : "password"}
                        value={passwords.newPassword}
                        onChange={(e) => handleInputChange("newPassword", e.target.value)}
                        isInvalid={!!errors.newPassword}
                      />
                      <InputGroup.Text onClick={() => togglePasswordVisibility("newPassword")}>
                        {passwordVisibility.newPassword ? <FaEyeSlash /> : <FaEye />}
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.newPassword}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Col>

                  <Col md="12" className="mb-3">
                    <Form.Label className="data-font-size data-font-colour">
                      {t("confirmPasswordText")} <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={passwordVisibility.confirmPassword ? "text" : "password"}
                        value={passwords.confirmPassword}
                        onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                        isInvalid={!!errors.confirmPassword}
                      />
                      <InputGroup.Text onClick={() => togglePasswordVisibility("confirmPassword")}>
                        {passwordVisibility.confirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Col>
                </Row>
                <Divider />
                <Row className="justify-content-end">
                  <Col xs="auto">
                    <Button
                      variant="falcon-default"
                      size="sm"
                      className="me-3"
                      type="button"
                      onClick={handleDiscard}
                    >
                      {t("cancel")}
                    </Button>
                    <Button variant="primary" size="sm" type="submit">
                      {t("save")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
          </Modal>

          <EmailConfigurationDetailsModal
            initialSenderEmail={hasUserEmailConfig ? senderEmail : ""}
            initialSenderEmailPassword={hasUserEmailConfig ? senderEmailPassword : ""}
            initialSmtpServerDetails={hasUserEmailConfig ? smtpServerDetails : ""}
            initialPortDetails={hasUserEmailConfig ? portDetails : ""}
            show={showEmailConfigurationDetailsModal}
            onClose={handleEmailConfigurationDetailsCloseModal}
            onSave={handleEmailConfigurationDetailsSaveModal}
            smtpFieldErrors={smtpFieldErrors}
            onClearSmtpError={handleClearSmtpError}
            isSaving={isSavingEmailConfig}
          />

          <SignatureModal show={showSignatureModal} onClose={() => setShowSignatureModal(false)}
            setCompanySignature={setCompanySignature} userData={data} mode="admin"
          />

          {/* Language Modal */}
          {domainConfig.showLanguageSwitcher && (
          <Modal
            show={showLanguageModal}
            onHide={() => setShowLanguageModal(false)}
            backdrop="static"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>{t("UpdateLanguage")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Row className="align-items-center">
                  <Col xs={4}>
                    <Form.Label className="mb-0 data-font-size data-font-colour">
                      {t("SelectLanguage")}
                    </Form.Label>
                  </Col>
                  <Col xs={8}>
                    <Select
                      key={i18n.language}
                      className="custom-select-container custom-select-sm"
                      classNamePrefix="custom-select"
                      placeholder={t("select")}
                      isClearable={false}
                      options={languageOptions}
                      value={languageOptions.find((opt) => opt.value === tempLanguage)}
                      onChange={(selected) => setTempLanguage(selected ? selected.value : "")}
                      noOptionsMessage={() => t("noOptionsAvailable")}
                    />
                  </Col>
                </Row>
              </Form>
              <Divider />
              <div className="d-flex justify-content-end">
                <Button
                  variant="falcon-default"
                  size="sm"
                  className="me-3"
                  onClick={() => setShowLanguageModal(false)}
                >
                  {t("cancel")}
                </Button>
                <Button variant="primary" size="sm" onClick={handleSaveLanguage}>
                  {t("save")}
                </Button>
              </div>
            </Modal.Body>
          </Modal>
          )}

          <FinancialInformationConfigurationModal
            initialIbanDetails={ibanDetails ? ibanDetails : data?.IbanDetails || ""}
            initialGaccountDetails={gaccountDetails ? gaccountDetails : data?.GaccountDetails || ""}
            initialKvkDetails={kvkDetails ? kvkDetails : data?.KvkDetails || ""}
            initialBtwDetails={btwDetails ? btwDetails : data?.BtwDetails || ""}
            initialAccountDetails={accountDetails || data?.AccountDetails || ""}
            initialIfscDetails={ifscDetails || data?.IfscDetails || ""}
            initialGstDetails={gstDetails || data?.GstDetails || ""}
            finLabels={finLabels}
            show={showFinancialInformationConfigurationModal}
            onClose={handleFinancialInformationConfigurationCloseModal}
            onSave={handleFinancialInformationConfigurationSaveModal}
          />
        </>
      )}{" "}
    </>
  );
};

export default Profile;